/**
 * PATH: packages/email-hub/src/feature/components/PingNotificationCard.tsx
 * FILE_ID: EMAIL-COLLAB-COMPONENT-002
 * PURPOSE: Banner card shown to the RECIPIENT of a ping/handoff at the top of
 *          the email reader: "<Sender> pinged you (urgent): <note>" with an
 *          "Open on Kanban board →" CTA that deep-links to the allocated card.
 * WHY:     Operator (2026-06-03): "When you have tagged a teammate / ping them,
 *          the notification should come into the email as a card link where you
 *          can go to the kanban board after clicking it." The ping backend
 *          (EmailHandoff) + the "assigned to me" list (GET /api/emails/
 *          assignments/) already exist and had NO in-app surface — this is it.
 *          Change #269: detection was extracted to `usePingHandoff` so this
 *          component is now a pure presentational leaf driven by a `handoff`
 *          prop — see the mount-effect note below for why that matters.
 * FLOW:
 *   ┌────────────────────┐   ┌──────────────────┐   ┌─────────────────────┐
 *   │ caller detects via │ → │ passes `handoff` │ → │ banner + kanban CTA, │
 *   │ usePingHandoff     │   │ prop (or null)    │   │ stamps viewed on mount│
 *   └────────────────────┘   └──────────────────┘   └─────────────────────┘
 * DEPENDENCIES:
 *   - ./usePingHandoff (MyHandoff type; callers own the detection fetch)
 *   - ../lib/api (emailApi): POST view/dismiss only — no GET here anymore
 *   - ../lib/kanbanDeepLink: build /kanban/<board>?card=<card>
 * RELATED:
 *   - PingTeammateButton.tsx: the sender side (creates the handoff)
 *   - organisation/views/email_views/assignment_views.py: the endpoints
 *   - reader-header/adapters/ReaderHeaderAdapter.tsx: mounts this ONLY when
 *     the operator expands the `ping` Notice Center entry — see the
 *     mount-effect note below.
 * NOTES FOR FUTURE AI: The "viewed" stamp fires from THIS component's mount
 *   effect, not from detection (`usePingHandoff`). Whether that means
 *   "immediately" or "on expand" is entirely up to WHEN the caller mounts
 *   this component — do not move the stamp back into the detection hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellRing, ExternalLink, X } from 'lucide-react'
import { emailApi } from '../lib/api'
import { cn } from '../../_internal/lib/utils'
import { buildKanbanCardLink, type KanbanLinkSource } from '../lib/kanbanDeepLink'
import type { MyHandoff } from './usePingHandoff'

export function PingNotificationCard({
  emailId,
  allocationSummary,
  handoff,
}: {
  emailId: string
  /** From `email.allocation_summary` — gives the board/card to deep-link to. */
  allocationSummary?: KanbanLinkSource | null
  /**
   * The detected handoff (via `usePingHandoff`), or null when there is none
   * for this email. This component no longer fetches — the caller decides
   * both WHETHER there is a ping to show and WHEN this component mounts.
   * That mount timing is load-bearing: the effect below stamps "viewed" for
   * the audit trail exactly once per handoff, so a caller that mounts this
   * card immediately on detection gets an immediate stamp (legacy flag-OFF
   * surfaces, unchanged behaviour), while a caller that mounts it only
   * inside an expanded Notice Center body (ReaderHeaderAdapter, flag-ON)
   * gets a stamp that means "the operator opened it."
   */
  handoff: MyHandoff | null
}) {
  const navigate = useNavigate()
  const [dismissedLocally, setDismissedLocally] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const viewedStampedForId = useRef<string | null>(null)

  useEffect(() => {
    setDismissedLocally(false)
  }, [emailId])

  useEffect(() => {
    if (!handoff || viewedStampedForId.current === handoff.id) return
    viewedStampedForId.current = handoff.id
    // Fire-and-forget audit stamp (idempotent server-side). See the file
    // header + prop doc above: this fires on THIS mount/handoff-arrival, so
    // its real-world timing is entirely caller-controlled.
    emailApi(`/api/emails/assignments/${handoff.id}/view/`, {
      method: 'POST',
    }).catch(() => {})
  }, [handoff])

  const dismiss = useCallback(async () => {
    if (!handoff || dismissing) return
    setDismissing(true)
    try {
      await emailApi(`/api/emails/assignments/${handoff.id}/dismiss/`, {
        method: 'POST',
      })
      setDismissedLocally(true)
    } catch {
      setDismissing(false)
    }
  }, [handoff, dismissing])

  if (!handoff || dismissedLocally) return null

  const kanbanLink = buildKanbanCardLink(allocationSummary)
  const who = (handoff.assigned_by_name || 'A teammate').trim()

  return (
    <div
      className={cn(
        'mb-4 rounded-xl border border-rose-500/40 bg-rose-500/10',
        'p-3 flex items-start gap-3',
      )}
      data-testid="ping-notification-card"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-500/20 text-rose-600 dark:text-rose-300">
        <BellRing className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-foreground">
          {who} pinged you
        </p>
        {handoff.message && (
          <p className="mt-0.5 text-[12px] text-muted-foreground whitespace-pre-line break-words">
            {handoff.message}
          </p>
        )}
        {kanbanLink && (
          <button
            type="button"
            onClick={() => navigate(kanbanLink)}
            className={cn(
              'mt-2 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-medium',
              'bg-rose-600 text-white hover:bg-rose-600/90 transition-colors',
            )}
            data-testid="ping-notification-open-board"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Open on Kanban board
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => void dismiss()}
        disabled={dismissing}
        title="Dismiss"
        aria-label="Dismiss ping"
        className="p-1 rounded-md text-muted-foreground hover:bg-rose-500/15 disabled:opacity-50"
        data-testid="ping-notification-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export default PingNotificationCard
